import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { logout } from '../features/auth/authSlice';
import { clearCart, fetchCart } from '../features/cart/cartSlice';
import { fetchCategoriesWithSubCategories } from '../features/categories/categoriesSlice';
import YeloooLogo from '../images/YeloooLogo.png';
import { API_URL, BASE_URL } from '../config';
import { useFooterInfoOptional } from './FooterInfoContext';
import './Header.css';

const moreMenuItems = [
  { label: 'About Us', to: '/about' },
  { label: 'Contact Us', to: '/contact' },
  { label: 'Careers', to: '/careers' },
  { label: 'Yelooo Stories', to: '/stories' },
  { label: 'Press', to: '/press' },
  { label: 'Corporate Information', to: '/corporate' },
  { label: 'Payments', to: '/payments' },
  { label: 'Shipping', to: '/shipping' },
  { label: 'Cancellation & Returns', to: '/cancellation' },
  { label: 'Terms Of Use', to: '/terms' },
  { label: 'Security', to: '/security' },
  { label: 'Privacy', to: '/privacy' },
  { label: 'Sitemap', to: '/sitemap' },
  { label: 'Grievance Redressal', to: '/grievance' },
  { label: 'EPR Compliance', to: '/epr' },
  { label: 'FAQ', to: '/faq' },
];

const compactMoreMenuItems = moreMenuItems.map((item) => {
  const compactLabels = {
    'About Us': 'About',
    'Contact Us': 'Contact',
    'Yelooo Stories': 'Stories',
    'Corporate Information': 'Corporate',
    'Cancellation & Returns': 'Cancellation',
    'Terms Of Use': 'Terms',
    'Grievance Redressal': 'Grievance',
    'EPR Compliance': 'EPR',
  };

  return {
    ...item,
    label: compactLabels[item.label] || item.label,
  };
});

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const footerInfo = useFooterInfoOptional();

  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userRole = useSelector((state) => state.auth.userRole);
  const username = useSelector((state) => state.auth.username);
  const cartItems = useSelector((state) => state.cart.items);
  const cartStatus = useSelector((state) => state.cart.status);
  const categories = useSelector((state) => state.categories.itemsWithSubCategories);
  const categoriesStatus = useSelector((state) => state.categories.subCategoriesStatus);

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeSubCategory, setActiveSubCategory] = useState(null);
  const [activeTertiaryCategory, setActiveTertiaryCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [profileDisplayName, setProfileDisplayName] = useState('');
  const [profileUserCode, setProfileUserCode] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpandedKeys, setMobileExpandedKeys] = useState(() => new Set());

  const isMobileExpanded = (key) => mobileExpandedKeys.has(key);

  const toggleMobileExpand = (key) => {
    setMobileExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileExpandedKeys(new Set());
  };

  const openInfoPageInPanel = (path) => {
    closeDropdown();
    closeMobileMenu();
    if (footerInfo) {
      footerInfo.openInfoPanel(path);
    } else {
      navigate(path);
    }
  };

  useEffect(() => {
    if (categoriesStatus === 'idle') {
      dispatch(fetchCategoriesWithSubCategories());
    }
  }, [categoriesStatus, dispatch]);

  // Rehydrate cart from server on any page load when user is logged in as customer (fixes cart count reset on refresh)
  useEffect(() => {
    if (isLoggedIn && userRole !== 'Admin' && userRole !== 'Seller' && cartStatus === 'idle') {
      dispatch(fetchCart());
    }
  }, [isLoggedIn, userRole, cartStatus, dispatch]);

  // Fetch profile image when logged in
  useEffect(() => {
    const fetchProfileImage = async () => {
      if (isLoggedIn) {
        try {
          const token = localStorage.getItem('jwtToken');
          const response = await axios.get(`${API_URL}/UserProfile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data?.profileImageUrl) {
            setProfileImageUrl(response.data.profileImageUrl);
          }
          setProfileDisplayName(response.data?.fullName || response.data?.username || username || '');
          setProfileUserCode(response.data?.referralCode || '');
        } catch (err) {
          console.error('Failed to fetch profile image:', err);
        }
      } else {
        setProfileImageUrl(null);
        setProfileDisplayName('');
        setProfileUserCode('');
      }
    };
    fetchProfileImage();
  }, [isLoggedIn, username]);

  const handleLogout = () => {
    setActiveDropdown(null);
    dispatch(logout());
    dispatch(clearCart());
    navigate('/');
  };

  const toggleDropdown = (menu) => {
    setActiveDropdown(activeDropdown === menu ? null : menu);
  };

  const closeDropdown = () => {
    setActiveDropdown(null);
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/category/${categoryId}`);
    closeAllCategoryMenus();
  };

  const handleSubCategoryClick = (subCategoryId, parentCategoryId) => {
    if (parentCategoryId) {
      navigate(`/category/${parentCategoryId}?subcategory=${subCategoryId}`);
    } else {
      const parent = (categories || []).find((c) =>
        (c.subCategories || []).some((s) => s.subCategoryId === subCategoryId)
      );
      if (parent) {
        navigate(`/category/${parent.categoryId}?subcategory=${subCategoryId}`);
      } else {
        navigate(`/category/0?subcategory=${subCategoryId}`);
      }
    }
    closeAllCategoryMenus();
  };

  const handleTertiaryCategoryClick = (tertiaryCategoryId, parentCategoryId) => {
    if (parentCategoryId) {
      navigate(`/category/${parentCategoryId}?tertiary=${tertiaryCategoryId}`);
    } else {
      navigate(`/?tertiary=${tertiaryCategoryId}`);
    }
    closeAllCategoryMenus();
  };

  const handleQuaternaryCategoryClick = (quaternaryCategoryId, parentCategoryId) => {
    if (parentCategoryId) {
      navigate(`/category/${parentCategoryId}?quaternary=${quaternaryCategoryId}`);
    } else {
      navigate(`/?quaternary=${quaternaryCategoryId}`);
    }
    closeAllCategoryMenus();
  };

  const closeAllCategoryMenus = () => {
    setActiveCategory(null);
    setActiveSubCategory(null);
    setActiveTertiaryCategory(null);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get('search');
    setSearchQuery(fromUrl != null && fromUrl !== '' ? fromUrl : '');
  }, [location.search]);

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.trim() === '') {
      const params = new URLSearchParams(location.search);
      if (params.has('search')) {
        navigate('/');
      }
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      const params = new URLSearchParams(location.search);
      if (params.has('search')) {
        navigate('/');
      }
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(e);
    }
  };

  const renderMobileAccordionToggle = (key, label, { level = 0, className = '', accentChevron = false } = {}) => {
    const open = isMobileExpanded(key);
    return (
      <button
        type="button"
        className={`header-mobile-accordion-toggle level-${level} ${open ? 'is-open' : ''} ${accentChevron ? 'accent-chevron' : ''} ${className}`.trim()}
        onClick={() => toggleMobileExpand(key)}
        aria-expanded={open}
      >
        <span className="header-mobile-accordion-label">{label}</span>
        <span className="header-mobile-accordion-chevron" aria-hidden="true" />
      </button>
    );
  };

  const handleDrawerSearch = (e) => {
    handleSearch(e);
    closeMobileMenu();
  };

  const renderMobileAccordionPanel = (key, children, { level = 0 } = {}) =>
    isMobileExpanded(key) ? (
      <div className={`header-mobile-accordion-panel level-${level}`} role="region">
        {children}
      </div>
    ) : null;

  const renderMobileNavLink = (to, label, { onNavigate, className = '' } = {}) => (
    <Link
      to={to}
      className={`header-mobile-drawer-item header-mobile-nav-link ${className}`.trim()}
      onClick={() => {
        if (onNavigate) onNavigate();
        closeMobileMenu();
      }}
    >
      {label}
    </Link>
  );

  const renderMobileCategoryTree = () => {
    if (!categories || categories.length === 0) return null;

    return (
      <div className="header-mobile-accordion-group">
        {renderMobileAccordionToggle('section-categories', 'Categories', { level: 0 })}
        {renderMobileAccordionPanel(
          'section-categories',
          <div className="header-mobile-nested-list">
            {categories.map((category) => {
              const subList = Array.isArray(category.subCategories) ? category.subCategories : [];
              const catKey = `cat-${category.categoryId}`;
              const hasChildren = subList.length > 0;

              if (!hasChildren) {
                return (
                  <button
                    key={category.categoryId}
                    type="button"
                    className="header-mobile-drawer-item header-mobile-leaf-item level-1"
                    onClick={() => {
                      handleCategoryClick(category.categoryId);
                      closeMobileMenu();
                    }}
                  >
                    {category.categoryName}
                  </button>
                );
              }

              return (
                <div key={category.categoryId} className="header-mobile-nested-branch">
                  {renderMobileAccordionToggle(catKey, category.categoryName, { level: 1 })}
                  {renderMobileAccordionPanel(
                    catKey,
                    <div className="header-mobile-nested-list">
                      <button
                        type="button"
                        className="header-mobile-drawer-item header-mobile-view-all level-2"
                        onClick={() => {
                          handleCategoryClick(category.categoryId);
                          closeMobileMenu();
                        }}
                      >
                        All {category.categoryName}
                      </button>
                      {subList.map((subCategory) => {
                        const tertiaryList = Array.isArray(subCategory.tertiaryCategories)
                          ? subCategory.tertiaryCategories
                          : [];
                        const subKey = `sub-${subCategory.subCategoryId}`;
                        const subHasChildren = tertiaryList.length > 0;

                        if (!subHasChildren) {
                          return (
                            <button
                              key={subCategory.subCategoryId}
                              type="button"
                              className="header-mobile-drawer-item header-mobile-leaf-item level-2"
                              onClick={() => {
                                handleSubCategoryClick(subCategory.subCategoryId, category.categoryId);
                                closeMobileMenu();
                              }}
                            >
                              {subCategory.subCategoryName}
                            </button>
                          );
                        }

                        return (
                          <div key={subCategory.subCategoryId} className="header-mobile-nested-branch">
                            {renderMobileAccordionToggle(subKey, subCategory.subCategoryName, { level: 2 })}
                            {renderMobileAccordionPanel(
                              subKey,
                              <div className="header-mobile-nested-list">
                                <button
                                  type="button"
                                  className="header-mobile-drawer-item header-mobile-view-all level-3"
                                  onClick={() => {
                                    handleSubCategoryClick(subCategory.subCategoryId, category.categoryId);
                                    closeMobileMenu();
                                  }}
                                >
                                  All {subCategory.subCategoryName}
                                </button>
                                {tertiaryList.map((tertiary) => {
                                  const quaternaryList = Array.isArray(tertiary.quaternaryCategories)
                                    ? tertiary.quaternaryCategories
                                    : [];
                                  const tertKey = `tert-${tertiary.tertiaryCategoryId}`;
                                  const tertHasChildren = quaternaryList.length > 0;

                                  if (!tertHasChildren) {
                                    return (
                                      <button
                                        key={tertiary.tertiaryCategoryId}
                                        type="button"
                                        className="header-mobile-drawer-item header-mobile-leaf-item level-3"
                                        onClick={() => {
                                          handleTertiaryCategoryClick(tertiary.tertiaryCategoryId, category.categoryId);
                                          closeMobileMenu();
                                        }}
                                      >
                                        {tertiary.tertiaryCategoryName}
                                      </button>
                                    );
                                  }

                                  return (
                                    <div key={tertiary.tertiaryCategoryId} className="header-mobile-nested-branch">
                                      {renderMobileAccordionToggle(tertKey, tertiary.tertiaryCategoryName, {
                                        level: 3,
                                      })}
                                      {renderMobileAccordionPanel(
                                        tertKey,
                                        <div className="header-mobile-nested-list">
                                          <button
                                            type="button"
                                            className="header-mobile-drawer-item header-mobile-view-all level-4"
                                            onClick={() => {
                                              handleTertiaryCategoryClick(tertiary.tertiaryCategoryId, category.categoryId);
                                              closeMobileMenu();
                                            }}
                                          >
                                            All {tertiary.tertiaryCategoryName}
                                          </button>
                                          {quaternaryList.map((quaternary) => (
                                            <button
                                              key={quaternary.quaternaryCategoryId}
                                              type="button"
                                              className="header-mobile-drawer-item header-mobile-leaf-item level-4"
                                              onClick={() => {
                                                handleQuaternaryCategoryClick(quaternary.quaternaryCategoryId, category.categoryId);
                                                closeMobileMenu();
                                              }}
                                            >
                                              {quaternary.quaternaryCategoryName}
                                            </button>
                                          ))}
                                        </div>,
                                        { level: 3 }
                                      )}
                                    </div>
                                  );
                                })}
                              </div>,
                              { level: 2 }
                            )}
                          </div>
                        );
                      })}
                    </div>,
                    { level: 1 }
                  )}
                </div>
              );
            })}
          </div>,
          { level: 0 }
        )}
      </div>
    );
  };

  return (
    <header className="app-header">
      {/* Mobile: compact 2-3 row layout (Amazon style) */}
      <div className="header-mobile">
        <div className="header-mobile-row1">
          <button
            type="button"
            className="header-hamburger"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
            </svg>
          </button>
          <Link to="/" className="logo logo-mobile" onClick={closeMobileMenu}>
            <img src={YeloooLogo} alt="Yelooo" className="logo-image" />
          </Link>
          <div className="header-mobile-icons">
            {!isLoggedIn ? (
              <Link to="/login" className="header-mobile-signin" onClick={closeMobileMenu}>
                Sign in <span aria-hidden="true">&gt;</span>
              </Link>
            ) : (
              <button
                type="button"
                className="header-mobile-account"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Account"
              >
                {profileImageUrl ? (
                  <img src={`${BASE_URL}${profileImageUrl}`} alt={username} className="profile-icon-img" />
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                  </svg>
                )}
              </button>
            )}
            {(!isLoggedIn || (userRole !== 'Admin' && userRole !== 'Seller')) && (
              <Link to="/cart" className="header-mobile-cart" onClick={closeMobileMenu}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                  <circle cx="9" cy="21" r="1"/>
                  <circle cx="20" cy="21" r="1"/>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                </svg>
                {cartItems.reduce((total, item) => total + item.quantity, 0) > 0 && (
                  <span className="header-cart-badge">{cartItems.reduce((total, item) => total + item.quantity, 0)}</span>
                )}
              </Link>
            )}
          </div>
        </div>
        <div className="header-mobile-row2">
          <form onSubmit={handleSearch} className="header-mobile-search-form">
            <div className="header-mobile-search-inner">
              <input
                type="text"
                className="header-mobile-search-input"
                placeholder="Search for products, brands and more"
                value={searchQuery}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchKeyPress}
              />
              <button type="submit" className="header-mobile-search-btn" aria-label="Search">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="m21 21-4.35-4.35"/>
                </svg>
              </button>
            </div>
          </form>
        </div>

      </div>

      {/* Mobile hamburger menu overlay */}
      {mobileMenuOpen && (
        <>
          <div className="header-mobile-overlay" onClick={closeMobileMenu} aria-hidden="true" />
          <div className="header-mobile-drawer">
            <div className="header-mobile-drawer-top">
              <form onSubmit={handleDrawerSearch} className="header-mobile-drawer-search-form">
                <input
                  type="text"
                  className="header-mobile-drawer-search-input"
                  placeholder="Search Product, Categories, Brand..."
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onKeyPress={handleSearchKeyPress}
                />
                <span className="header-mobile-drawer-search-divider" aria-hidden="true" />
                <button type="submit" className="header-mobile-drawer-search-btn" aria-label="Search">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                </button>
              </form>
              <button type="button" className="header-mobile-drawer-close" onClick={closeMobileMenu} aria-label="Close menu">
                <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>
            <div className="header-mobile-drawer-body header-mobile-accordion-nav">
              {userRole !== 'Seller' && (
                <>
                  {renderMobileCategoryTree()}
                  <div className="header-mobile-accordion-group">
                    {renderMobileAccordionToggle('section-brands', 'Brands')}
                    {renderMobileAccordionPanel(
                      'section-brands',
                      <>
                        {renderMobileNavLink('/shop', 'Shop All Brands', { className: 'level-1' })}
                        {renderMobileNavLink('/sellers-in-area', 'Sellers Near Me', { className: 'level-1' })}
                      </>
                    )}
                  </div>
                  <div className="header-mobile-accordion-group">
                    {renderMobileAccordionToggle('section-shop', 'Shop')}
                    {renderMobileAccordionPanel(
                      'section-shop',
                      <>
                        {renderMobileNavLink('/shop', 'Shop All', { className: 'level-1' })}
                        {renderMobileNavLink('/sellers-in-area', 'Sellers Near Me', { className: 'level-1' })}
                      </>
                    )}
                  </div>
                </>
              )}
              {userRole !== 'Admin' && userRole !== 'Seller' && (
                <div className="header-mobile-accordion-group">
                  {renderMobileAccordionToggle('section-more', 'More', { accentChevron: true })}
                  {renderMobileAccordionPanel(
                    'section-more',
                    moreMenuItems.map((item) => (
                      <button
                        key={item.to}
                        type="button"
                        className="header-mobile-drawer-item level-1"
                        onClick={() => openInfoPageInPanel(item.to)}
                      >
                        {item.label}
                      </button>
                    ))
                  )}
                </div>
              )}
              {!isLoggedIn && (
                <div className="header-mobile-accordion-group header-mobile-auth-group">
                  {renderMobileNavLink('/login', 'Sign In', { className: 'level-0' })}
                  {renderMobileNavLink('/register', 'Register', { className: 'level-0' })}
                </div>
              )}
              {isLoggedIn && userRole === 'Admin' && (
                <>
                  <div className="header-mobile-accordion-group">
                    {renderMobileAccordionToggle('admin-product', 'Manage Product')}
                    {renderMobileAccordionPanel(
                      'admin-product',
                      <>
                        {renderMobileNavLink('/add-product', 'Add Product', { className: 'level-1' })}
                        {renderMobileNavLink('/admin/categories', 'Manage Categories', { className: 'level-1' })}
                        {renderMobileNavLink('/admin/attributes', 'Sizes & Colors', { className: 'level-1' })}
                        {renderMobileNavLink('/', 'Product List', { className: 'level-1' })}
                      </>
                    )}
                  </div>
                  <div className="header-mobile-accordion-group">
                    {renderMobileAccordionToggle('admin-seller', 'Manage Seller')}
                    {renderMobileAccordionPanel(
                      'admin-seller',
                      <>
                        {renderMobileNavLink('/admin/add-seller', 'Add Seller', { className: 'level-1' })}
                        {renderMobileNavLink('/admin/sellers', 'Seller List', { className: 'level-1' })}
                        {renderMobileNavLink('/admin/customers', 'Customer List', { className: 'level-1' })}
                      </>
                    )}
                  </div>
                  <div className="header-mobile-accordion-group">
                    {renderMobileAccordionToggle('admin-reports', 'Reports')}
                    {renderMobileAccordionPanel(
                      'admin-reports',
                      <>
                        {renderMobileNavLink('/admin/reports/orders', 'Product Ordered', { className: 'level-1' })}
                        {renderMobileNavLink('/admin/reports/points', 'Points Report', { className: 'level-1' })}
                        {renderMobileNavLink('/admin/points-benefits', 'Points & Benefits', { className: 'level-1' })}
                        {renderMobileNavLink('/admin/reports/commission', 'Commission Report', { className: 'level-1' })}
                      </>
                    )}
                  </div>
                  <div className="header-mobile-accordion-group">
                    {renderMobileAccordionToggle('admin-other', 'Other')}
                    {renderMobileAccordionPanel(
                      'admin-other',
                      renderMobileNavLink('/admin/kyc', 'KYC Management', { className: 'level-1' })
                    )}
                  </div>
                </>
              )}
              {isLoggedIn && userRole === 'Seller' && (
                <div className="header-mobile-accordion-group">
                  {renderMobileAccordionToggle('section-account', 'My Account')}
                  {renderMobileAccordionPanel(
                    'section-account',
                    <>
                      {renderMobileNavLink('/profile', 'My Profile', { className: 'level-1' })}
                      {renderMobileNavLink('/seller/orders', 'My Orders', { className: 'level-1' })}
                      {renderMobileNavLink('/seller/commission', 'My Commission', { className: 'level-1' })}
                      {renderMobileNavLink('/seller/offline-approvals', 'Approve Offline Purchases', { className: 'level-1' })}
                    </>
                  )}
                </div>
              )}
              {isLoggedIn && userRole === 'Customer' && (
                <div className="header-mobile-accordion-group">
                  {renderMobileAccordionToggle('section-account', 'My Account')}
                  {renderMobileAccordionPanel(
                    'section-account',
                    <>
                      {renderMobileNavLink('/profile', 'My Profile', { className: 'level-1' })}
                      {renderMobileNavLink('/my-orders', 'My Orders', { className: 'level-1' })}
                      {renderMobileNavLink('/my-network', 'My Network', { className: 'level-1' })}
                      {renderMobileNavLink('/my-points', 'My Points', { className: 'level-1' })}
                      {renderMobileNavLink('/my-wallet', 'My Wallet', { className: 'level-1' })}
                      {renderMobileNavLink('/offline-purchase', 'Offline Purchase', { className: 'level-1' })}
                      {renderMobileNavLink('/sellers-in-area', 'Sellers in My Area', { className: 'level-1' })}
                    </>
                  )}
                </div>
              )}
              {isLoggedIn && (
                <button
                  type="button"
                  className="header-mobile-drawer-item header-mobile-drawer-logout"
                  onClick={() => {
                    handleLogout();
                    closeMobileMenu();
                  }}
                >
                  Logout
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Desktop: main header row */}
      <div className="header-content header-desktop">
        <Link to="/" className="logo">
          <img src={YeloooLogo} alt="Yelooo" className="logo-image" />
        </Link>

        <nav className="header-nav">
            {userRole !== 'Admin' && userRole !== 'Seller' && categories && categories.length > 0 && (
              <div
                className="dropdown header-nav-category"
                onMouseLeave={() => {
                  closeDropdown();
                  closeAllCategoryMenus();
                }}
              >
                <button
                  className="dropdown-toggle"
                  onClick={() => toggleDropdown('shop')}
                  onMouseEnter={() => setActiveDropdown('shop')}
                >
                  Shop ▾
                </button>
                {activeDropdown === 'shop' && (
                  <div className="dropdown-menu category-dropdown-menu">
                    {categories.map((category) => (
                      <div
                        key={category.categoryId}
                        className={`subcategory-item-wrapper ${activeCategory === category.categoryId ? 'active' : ''}`}
                        onMouseEnter={() => setActiveCategory(category.categoryId)}
                      >
                        <button
                          className="subcategory-item"
                          onClick={() => {
                            handleCategoryClick(category.categoryId);
                            closeDropdown();
                          }}
                        >
                          {category.categoryName}
                          {Array.isArray(category.subCategories) && category.subCategories.length > 0 && (
                            <span className="submenu-arrow">›</span>
                          )}
                        </button>

                        {activeCategory === category.categoryId && Array.isArray(category.subCategories) && category.subCategories.length > 0 && (
                          <div className="tertiary-dropdown">
                            {category.subCategories.map((subCategory) => (
                              <div
                                key={subCategory.subCategoryId}
                                className={`tertiary-item-wrapper ${activeSubCategory === subCategory.subCategoryId ? 'active' : ''}`}
                                onMouseEnter={() => setActiveSubCategory(subCategory.subCategoryId)}
                              >
                                <button
                                  className="tertiary-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSubCategoryClick(subCategory.subCategoryId, category.categoryId);
                                    closeDropdown();
                                  }}
                                >
                                  {subCategory.subCategoryName}
                                  {Array.isArray(subCategory.tertiaryCategories) && subCategory.tertiaryCategories.length > 0 && (
                                    <span className="submenu-arrow">›</span>
                                  )}
                                </button>

                                {activeSubCategory === subCategory.subCategoryId && Array.isArray(subCategory.tertiaryCategories) && subCategory.tertiaryCategories.length > 0 && (
                                  <div className="quaternary-dropdown">
                                    {subCategory.tertiaryCategories.map((tertiary) => (
                                      <div
                                        key={tertiary.tertiaryCategoryId}
                                        className={`quaternary-item-wrapper ${activeTertiaryCategory === tertiary.tertiaryCategoryId ? 'active' : ''}`}
                                        onMouseEnter={() => setActiveTertiaryCategory(tertiary.tertiaryCategoryId)}
                                      >
                                        <button
                                          className="quaternary-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleTertiaryCategoryClick(tertiary.tertiaryCategoryId, category.categoryId);
                                            closeDropdown();
                                          }}
                                        >
                                          {tertiary.tertiaryCategoryName}
                                          {Array.isArray(tertiary.quaternaryCategories) && tertiary.quaternaryCategories.length > 0 && (
                                            <span className="submenu-arrow">›</span>
                                          )}
                                        </button>

                                        {activeTertiaryCategory === tertiary.tertiaryCategoryId && Array.isArray(tertiary.quaternaryCategories) && tertiary.quaternaryCategories.length > 0 && (
                                          <div className="quaternary-dropdown nested-quaternary-dropdown">
                                            {tertiary.quaternaryCategories.map((quaternary) => (
                                              <button
                                                key={quaternary.quaternaryCategoryId}
                                                className="quaternary-item"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleQuaternaryCategoryClick(quaternary.quaternaryCategoryId, category.categoryId);
                                                  closeDropdown();
                                                }}
                                              >
                                                {quaternary.quaternaryCategoryName}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {userRole !== 'Admin' && userRole !== 'Seller' && (
              <Link to="/sellers-in-area" className="nav-link">Sellers</Link>
            )}

            {userRole !== 'Admin' && userRole !== 'Seller' && (
              <div className="dropdown" onMouseLeave={closeDropdown}>
                <button
                  className="dropdown-toggle"
                  onClick={() => toggleDropdown('more')}
                  onMouseEnter={() => setActiveDropdown('more')}
                >
                  More ▾
                </button>
                {activeDropdown === 'more' && (
                  <div className="dropdown-menu more-dropdown-menu">
                    {compactMoreMenuItems.map((item) => (
                      <button
                        key={item.to}
                        type="button"
                        className="dropdown-item"
                        onClick={() => openInfoPageInPanel(item.to)}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
        </nav>

        <div className="search-box-container">
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              className="search-input"
              placeholder="Search products, brands..."
              value={searchQuery}
              onChange={handleSearchInputChange}
              onKeyPress={handleSearchKeyPress}
            />
            <button type="submit" className="search-button" aria-label="Search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </button>
          </form>
        </div>

        <nav className="header-nav header-nav-actions">
            {!isLoggedIn && (
              <>
                <Link to="/login" className="nav-link">Sign In</Link>
                <Link to="/register" className="nav-link">Join</Link>
              </>
            )}
            
            {/* Admin Dropdown Menus */}
            {isLoggedIn && userRole === 'Admin' && (
              <div className="admin-menus">
                {/* Manage Product Dropdown */}
                <div className="dropdown" onMouseLeave={closeDropdown}>
                  <button 
                    className="dropdown-toggle"
                    onClick={() => toggleDropdown('product')}
                    onMouseEnter={() => setActiveDropdown('product')}
                  >
                    Manage Product ▾
                  </button>
                  {activeDropdown === 'product' && (
                    <div className="dropdown-menu">
                      <Link to="/add-product" className="dropdown-item" onClick={closeDropdown}>
                        Add Product
                      </Link>
                      <Link to="/admin/categories" className="dropdown-item" onClick={closeDropdown}>
                        Manage Categories
                      </Link>
                      <Link to="/admin/attributes" className="dropdown-item" onClick={closeDropdown}>
                        Sizes &amp; Colors
                      </Link>
                      <Link to="/" className="dropdown-item" onClick={closeDropdown}>
                        Product List
                      </Link>
                    </div>
                  )}
                </div>

                {/* Manage Seller Dropdown */}
                <div className="dropdown" onMouseLeave={closeDropdown}>
                  <button 
                    className="dropdown-toggle"
                    onClick={() => toggleDropdown('seller')}
                    onMouseEnter={() => setActiveDropdown('seller')}
                  >
                    Manage Seller ▾
                  </button>
                  {activeDropdown === 'seller' && (
                    <div className="dropdown-menu">
                      <Link to="/admin/add-seller" className="dropdown-item" onClick={closeDropdown}>
                        Add Seller
                      </Link>
                      <Link to="/admin/sellers" className="dropdown-item" onClick={closeDropdown}>
                        Seller List
                      </Link>
                      <Link to="/admin/customers" className="dropdown-item" onClick={closeDropdown}>
                        Customer List
                      </Link>
                    </div>
                  )}
                </div>

                {/* Reports Dropdown */}
                <div className="dropdown" onMouseLeave={closeDropdown}>
                  <button 
                    className="dropdown-toggle"
                    onClick={() => toggleDropdown('reports')}
                    onMouseEnter={() => setActiveDropdown('reports')}
                  >
                    Reports ▾
                  </button>
                  {activeDropdown === 'reports' && (
                    <div className="dropdown-menu">
                      <Link to="/admin/reports/orders" className="dropdown-item" onClick={closeDropdown}>
                        Product Ordered
                      </Link>
                      <Link to="/admin/reports/points" className="dropdown-item" onClick={closeDropdown}>
                        Points Report
                      </Link>
                      <Link to="/admin/points-benefits" className="dropdown-item" onClick={closeDropdown}>
                        Points & Benefits
                      </Link>
                      <Link to="/admin/reports/commission" className="dropdown-item" onClick={closeDropdown}>
                        Commission Report
                      </Link>
                    </div>
                  )}
                </div>

                {/* KYC Management */}
                <Link to="/admin/kyc" className="nav-link">
                  KYC Management
                </Link>
              </div>
            )}

            {/* Seller Menus */}
            {isLoggedIn && userRole === 'Seller' && (
              <div className="seller-menus">
                <Link to="/seller/commission" className="nav-link">My Commission</Link>
                <Link to="/seller/offline-approvals" className="nav-link">Approve Offline Purchases</Link>
                {/* Offline purchase entry is only made by customer; seller no longer submits offline sale from here */}
                {/* <Link to="/seller/offline-sale" className="nav-link">Offline Sale</Link> */}
                <Link to="/seller/orders" className="nav-link">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                  </svg>
                  My Orders
                </Link>
              </div>
            )}

            {/* Show cart only for customers (not Admin or Seller) */}
            {(!isLoggedIn || (userRole !== 'Admin' && userRole !== 'Seller')) && (
              <Link to="/cart" className="cart-icon-link" title="Cart">
                <div className="cart-icon-wrapper">
                  <svg 
                    className="cart-icon" 
                    viewBox="0 0 24 24" 
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    width="28" 
                    height="28"
                  >
                    <circle cx="9" cy="21" r="1"></circle>
                    <circle cx="20" cy="21" r="1"></circle>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                  </svg>
                  {cartItems.reduce((total, item) => total + item.quantity, 0) > 0 && (
                    <span className="cart-badge">
                      {cartItems.reduce((total, item) => total + item.quantity, 0)}
                    </span>
                  )}
                </div>
              </Link>
            )}

            {/* Profile Icon with Dropdown - for logged in users */}
            {isLoggedIn && (
              <div className="dropdown profile-dropdown" onMouseLeave={closeDropdown}>
                <button 
                  className="profile-icon-btn"
                  onClick={() => toggleDropdown('profile')}
                  onMouseEnter={() => setActiveDropdown('profile')}
                  title={username}
                >
                  {profileImageUrl ? (
                    <img 
                      src={`${BASE_URL}${profileImageUrl}`} 
                      alt={username} 
                      className="profile-icon-img"
                    />
                  ) : (
                    <svg 
                      className="profile-icon" 
                      viewBox="0 0 24 24" 
                      fill="currentColor"
                      width="28" 
                      height="28"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                    </svg>
                  )}
                </button>
                {activeDropdown === 'profile' && (
                  <div className="dropdown-menu profile-menu">
                    <div className="profile-header">
                      {profileImageUrl ? (
                        <img 
                          src={`${BASE_URL}${profileImageUrl}`} 
                          alt={username} 
                          className="profile-avatar-img"
                        />
                      ) : (
                        <div className="profile-avatar-circle">
                          {username?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                      )}
                      <div className="profile-header-info">
                      <span className="profile-username">Welcome, {profileDisplayName || username}!</span>
                      {profileUserCode && <span className="profile-user-code">Code: {profileUserCode}</span>}
                      <span className="profile-role">{userRole}</span>
                      </div>
                    </div>
                    {userRole === 'Customer' && (
                      <>
                        <Link to="/profile" className="dropdown-item" onClick={closeDropdown}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                          </svg>
                          My Profile
                        </Link>
                        <Link to="/my-orders" className="dropdown-item" onClick={closeDropdown}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                          </svg>
                          My Orders
                        </Link>
                        <Link to="/my-network" className="dropdown-item" onClick={closeDropdown}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                          </svg>
                          My Network
                        </Link>
                        <Link to="/my-points" className="dropdown-item" onClick={closeDropdown}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"/>
                          </svg>
                          My Points
                        </Link>
                        <Link to="/my-wallet" className="dropdown-item" onClick={closeDropdown}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8z"/>
                          </svg>
                          My Wallet
                        </Link>
                        <Link to="/offline-purchase" className="dropdown-item" onClick={closeDropdown}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                          </svg>
                          Offline Purchase
                        </Link>
                        <Link to="/sellers-in-area" className="dropdown-item" onClick={closeDropdown}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                          </svg>
                          Sellers in My Area
                        </Link>
                      </>
                    )}
                    {userRole === 'Seller' && (
                      <>
                        <Link to="/profile" className="dropdown-item" onClick={closeDropdown}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                          </svg>
                          My Profile
                        </Link>
                        <Link to="/seller/orders" className="dropdown-item" onClick={closeDropdown}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                          </svg>
                          My Orders
                        </Link>
                        <Link to="/seller/commission" className="dropdown-item" onClick={closeDropdown}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"/>
                          </svg>
                          My Commission
                        </Link>
                        <Link to="/seller/offline-approvals" className="dropdown-item" onClick={closeDropdown}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                          </svg>
                          Approve Offline Purchases
                        </Link>
                        {/* Offline purchase entry is only made by customer; seller no longer submits offline sale from here */}
                        {/* <Link to="/seller/offline-sale" className="dropdown-item" onClick={closeDropdown}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                          </svg>
                          Offline Sale
                        </Link> */}
                      </>
                    )}
                    <button className="dropdown-item logout-item" onClick={handleLogout}>
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
                      </svg>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>

      </header>
  );
};

export default Header;
